/**
 * Saídas da operação: PDF do roteiro, pacote de parametrização (JSON + CSV de
 * teste QAS) e o envio para aprovação, que grava a operação em "Minhas operações".
 *
 * O PDF leva só os cards que o usuário marcar no modal, na ordem em que estão
 * na tela (que ele pode ter rearranjado).
 */

import { useMutation } from "@tanstack/react-query";
import { ClipboardCheck, Download, FileText, Package, Printer } from "lucide-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useOrdemCards } from "@/components/layout/CardsOrdenaveis";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useEnviarParaAprovacao } from "@/hooks/queries";
import { api, baixar } from "@/lib/api";
import type { PedidoCalculo } from "@/types";

/** Cards que podem ir para o PDF — ids iguais aos da pilha e às seções do backend. */
const SECOES_PDF = [
  { id: "resumo", rotulo: "Resumo da operação" },
  { id: "roteiro-fiscal", rotulo: "Roteiro fiscal" },
  { id: "roteiro-contabil", rotulo: "Roteiro contábil" },
] as const;
type SecaoPdf = (typeof SECOES_PDF)[number]["id"];

/** Pacote SAP e roteiro QAS ficam visíveis, mas desativados até serem liberados. */
const SAP_LIBERADO = false;

export function Entregaveis({
  pedido,
  variante,
}: {
  pedido: PedidoCalculo | null;
  variante?: string;
}) {
  const navegar = useNavigate();
  const enviar = useEnviarParaAprovacao();
  const [escolhendoSecoes, setEscolhendoSecoes] = React.useState(false);
  const [marcadas, setMarcadas] = React.useState<Set<SecaoPdf>>(
    () => new Set(SECOES_PDF.map((s) => s.id)),
  );

  const ordemTela = useOrdemCards();
  const secoesNaOrdem = React.useMemo(() => {
    const posicao = (id: string) => {
      const i = ordemTela?.indexOf(id) ?? -1;
      return i < 0 ? Number.MAX_SAFE_INTEGER : i;
    };
    return [...SECOES_PDF].sort((a, b) => posicao(a.id) - posicao(b.id));
  }, [ordemTela]);

  const alternar = (id: SecaoPdf, marcada: boolean) =>
    setMarcadas((atual) => {
      const nova = new Set(atual);
      if (marcada) nova.add(id);
      else nova.delete(id);
      return nova;
    });

  const baixarArquivo = useMutation({
    mutationFn: async ({
      rota,
      nome,
      extras,
    }: {
      rota: string;
      nome: string;
      extras?: Record<string, unknown>;
    }) => {
      if (!pedido) throw new Error("Desenhe a operação antes de gerar o arquivo.");
      const blob = await api<Blob>(rota, {
        method: "POST",
        body: { ...pedido, ...extras },
        blob: true,
      });
      baixar(blob, nome);
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const baixarJson = useMutation({
    mutationFn: async () => {
      if (!pedido) throw new Error("Desenhe a operação antes de gerar o pacote.");
      const pacote = await api<{ operacao: { id: string } }>("/fiscal/pacote", {
        method: "POST",
        body: pedido,
      });
      baixar(
        new Blob([JSON.stringify(pacote, null, 2)], { type: "application/json" }),
        `${pacote.operacao.id}-parametrizacao.json`,
      );
      return pacote;
    },
    onSuccess: () => toast.success("Pacote de parametrização gerado."),
    onError: (erro: Error) => toast.error(erro.message),
  });

  const enviarParaAprovacao = () => {
    if (!pedido) return;
    enviar.mutate({ ...pedido, variante }, {
      onSuccess: (envio) =>
        toast.success(`“${envio.nome}” enviada para aprovação.`, {
          action: { label: "Minhas operações", onClick: () => navegar("/minhas-operacoes") },
        }),
      onError: (erro: Error) => toast.error(erro.message),
    });
  };

  const desabilitado = !pedido || !pedido.nodes.length;

  return (
    <>
      <Card className="nao-imprimir">
        <CardHeader>
          <CardTitle>Entregáveis</CardTitle>
          <CardDescription>
            Documento para o TAX, pacote de parametrização SAP e roteiro de teste em QAS.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={desabilitado || baixarArquivo.isPending}
            onClick={() => setEscolhendoSecoes(true)}
          >
            <FileText />
            Baixar PDF do roteiro
          </Button>
          <Button variant="outline" disabled={desabilitado} onClick={() => window.print()}>
            <Printer />
            Imprimir
          </Button>
          <Button
            variant="outline"
            disabled={!SAP_LIBERADO || desabilitado || baixarJson.isPending}
            onClick={() => baixarJson.mutate()}
          >
            <Package />
            Pacote de parametrização (JSON)
          </Button>
          <Button
            variant="outline"
            disabled={!SAP_LIBERADO || desabilitado || baixarArquivo.isPending}
            onClick={() =>
              baixarArquivo.mutate({ rota: "/fiscal/pacote.csv", nome: "teste-qas.csv" })
            }
          >
            <Download />
            Roteiro de teste QAS (CSV)
          </Button>
          <Button
            disabled={desabilitado || enviar.isPending}
            onClick={enviarParaAprovacao}
          >
            <ClipboardCheck />
            Enviar para aprovação
          </Button>
        </CardContent>
      </Card>

      <Dialog open={escolhendoSecoes} onOpenChange={setEscolhendoSecoes}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>PDF do roteiro</DialogTitle>
            <DialogDescription>
              Marque o que entra no documento. Os cards saem na ordem em que estão na tela.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            {secoesNaOrdem.map((secao) => (
              <label
                key={secao.id}
                className="flex cursor-pointer items-center gap-3 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted/50"
              >
                <input
                  type="checkbox"
                  className="size-4 accent-primary"
                  checked={marcadas.has(secao.id)}
                  onChange={(e) => alternar(secao.id, e.target.checked)}
                />
                {secao.rotulo}
              </label>
            ))}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEscolhendoSecoes(false)}>
              Cancelar
            </Button>
            <Button
              disabled={!marcadas.size || baixarArquivo.isPending}
              onClick={() =>
                baixarArquivo.mutate(
                  {
                    rota: "/fiscal/pdf",
                    nome: "roteiro-fiscal.pdf",
                    extras: {
                      secoes: secoesNaOrdem.filter((s) => marcadas.has(s.id)).map((s) => s.id),
                    },
                  },
                  { onSuccess: () => setEscolhendoSecoes(false) },
                )
              }
            >
              <FileText />
              Baixar PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
